import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { UserApprovalQueue } from "@/components/admin/UserApprovalQueue";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAdminUsers } from "@/hooks/use-admin-users";
import { Search, UserCheck, UserX, ChevronLeft, ChevronRight, Copy, Check } from "lucide-react";
import { format } from "date-fns";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

const statusColors: Record<string, string> = {
  active: "bg-green-500/10 text-green-500 border-green-500/20",
  trial: "bg-blue-500/10 text-blue-500 border-blue-500/20",
  pending: "bg-orange-500/10 text-orange-500 border-orange-500/20",
  pending_approval: "bg-amber-500/10 text-amber-500 border-amber-500/20",
  onboarding_required: "bg-yellow-500/10 text-yellow-500 border-yellow-500/20",
  expired: "bg-gray-500/10 text-gray-500 border-gray-500/20",
  suspended: "bg-red-500/10 text-red-500 border-red-500/20",
  rejected: "bg-red-700/10 text-red-700 border-red-700/20",
  unverified: "bg-purple-500/10 text-purple-500 border-purple-500/20",
};

export default function AdminUsers() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "");
  const [searchTerm, setSearchTerm] = useState("");
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    type: "activate" | "deactivate";
    userId: string;
    userName: string;
  } | null>(null);

  const { users, total, page, pageSize, loading, fetchUsers, activateUser, deactivateUser } = useAdminUsers();

  const handleCopyUserId = async (userId: string) => {
    try {
      await navigator.clipboard.writeText(userId);
      setCopiedId(userId);
      toast.success("User ID copied");
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  useEffect(() => {
    fetchUsers(statusFilter || undefined, searchTerm || undefined, 1);
  }, [fetchUsers, statusFilter]);

  const handleSearch = () => {
    fetchUsers(statusFilter || undefined, searchTerm || undefined, 1);
  };

  const handleStatusChange = (value: string) => {
    setStatusFilter(value === "all" ? "" : value);
    if (value === "all") {
      searchParams.delete("status");
    } else {
      searchParams.set("status", value);
    }
    setSearchParams(searchParams);
  };

  const handleActivate = async () => {
    if (!confirmDialog) return;
    const success = await activateUser(confirmDialog.userId);
    if (success) {
      fetchUsers(statusFilter || undefined, searchTerm || undefined, page);
    }
    setConfirmDialog(null);
  };

  const handleDeactivate = async () => {
    if (!confirmDialog) return;
    const success = await deactivateUser(confirmDialog.userId);
    if (success) {
      fetchUsers(statusFilter || undefined, searchTerm || undefined, page);
    }
    setConfirmDialog(null);
  };

  const totalPages = Math.ceil(total / pageSize);

  const handleUserApproved = () => {
    fetchUsers(statusFilter || undefined, searchTerm || undefined, page);
  };

  return (
    <AdminLayout
      title="User Management"
      description="Activate, deactivate, and manage user accounts"
    >
      {/* Approval Queue Widget */}
      <UserApprovalQueue onUserApproved={handleUserApproved} />

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <CardTitle>Users ({total})</CardTitle>
            <div className="flex gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  className="pl-10 w-64"
                />
              </div>
              <Select value={statusFilter || "all"} onValueChange={handleStatusChange}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="pending_approval">Pending Approval</SelectItem>
                  <SelectItem value="onboarding_required">Onboarding Required</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="unverified">Unverified</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={handleSearch}>Search</Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading users...</div>
          ) : users.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">No users found</div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>User ID</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Reports</TableHead>
                    <TableHead>Tokens</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Trial Expires</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.user_id}>
                      <TableCell className="font-medium">
                        {user.full_name || "Unnamed User"}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {user.email || "-"}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-muted-foreground font-mono">
                            {user.user_id.slice(0, 8)}...
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => handleCopyUserId(user.user_id)}
                          >
                            {copiedId === user.user_id ? (
                              <Check className="h-3 w-3 text-green-500" />
                            ) : (
                              <Copy className="h-3 w-3 text-muted-foreground" />
                            )}
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={statusColors[user.status] || ""}>
                          {user.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{user.plan || "-"}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {user.reports_used.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-sm">
                        {user.tokens_used.toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{user.role || "none"}</Badge>
                      </TableCell>
                      <TableCell>
                        {user.trial_expires_at
                          ? format(new Date(user.trial_expires_at), "MMM d, yyyy")
                          : "-"
                        }
                      </TableCell>
                      <TableCell>
                        {format(new Date(user.created_at), "MMM d, yyyy")}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-2 justify-end">
                          {(user.status === "pending" || user.status === "pending_approval" || user.status === "onboarding_required" || user.status === "suspended" || user.status === "expired" || user.status === "rejected") && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-green-500 hover:text-green-600"
                              onClick={() => setConfirmDialog({
                                open: true,
                                type: "activate",
                                userId: user.user_id,
                                userName: user.full_name || "this user"
                              })}
                            >
                              <UserCheck className="h-4 w-4 mr-1" />
                              Activate
                            </Button>
                          )}
                          {(user.status === "active" || user.status === "trial") && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-500 hover:text-red-600"
                              onClick={() => setConfirmDialog({
                                open: true,
                                type: "deactivate",
                                userId: user.user_id,
                                userName: user.full_name || "this user"
                              })}
                            >
                              <UserX className="h-4 w-4 mr-1" />
                              Deactivate
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-muted-foreground">
                    Page {page} of {totalPages}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page <= 1}
                      onClick={() => fetchUsers(statusFilter || undefined, searchTerm || undefined, page - 1)}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page >= totalPages}
                      onClick={() => fetchUsers(statusFilter || undefined, searchTerm || undefined, page + 1)}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <AlertDialog open={!!confirmDialog} onOpenChange={() => setConfirmDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmDialog?.type === "activate" ? "Activate User" : "Deactivate User"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog?.type === "activate" 
                ? `Are you sure you want to activate ${confirmDialog?.userName}? They will be granted access to the platform.`
                : `Are you sure you want to deactivate ${confirmDialog?.userName}? They will lose access to the platform immediately.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDialog?.type === "activate" ? handleActivate : handleDeactivate}
              className={confirmDialog?.type === "deactivate" ? "bg-destructive hover:bg-destructive/90" : ""}
            >
              {confirmDialog?.type === "activate" ? "Activate" : "Deactivate"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
